import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { MASCOT } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { CheckCircle2, XCircle } from "lucide-react";

const MAX_TRIES = 40;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const triesRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      triesRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.existing) {
            toast.success("Assinatura ativa! Faça login para continuar.");
            setTimeout(() => navigate("/login"), 1200);
          } else if (data.signup_token) {
            setTimeout(() => navigate(`/criar-conta?token=${data.signup_token}`), 1000);
          } else {
            setTimeout(() => navigate("/login"), 1200);
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        /* mantém tentando */
      }
      if (triesRef.current >= MAX_TRIES) { setState("timeout"); return; }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate]);

  if (state === "error" || state === "timeout") {
    return (
      <AuthShell title="Pagamento não confirmado" subtitle="Não conseguimos confirmar seu pagamento ainda." mascot={MASCOT.tilt}>
        <div className="text-center space-y-4">
          <XCircle size={40} className="text-[#b91c1c] mx-auto" />
          <p className="text-sm text-[#5c4634]">Se o valor foi debitado, aguarde alguns instantes e faça login. Caso contrário, tente assinar novamente.</p>
          <button onClick={() => navigate("/")} className="btn-orange w-full py-3.5 rounded-full font-semibold" data-testid="payment-back-home">Voltar aos planos</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={state === "paid" ? "Pagamento confirmado!" : "Confirmando pagamento..."}
      subtitle={state === "paid" ? "Tudo certo! Vamos criar seu acesso." : "Só um instante enquanto a Solange confirma tudo."}
      mascot={MASCOT.happy}
    >
      <div className="text-center" data-testid="payment-success-status">
        {state === "paid" ? (
          <CheckCircle2 size={44} className="text-[#15803d] mx-auto" />
        ) : (
          <div className="flex justify-center gap-1.5">
            <span className="typing-dot h-2.5 w-2.5 rounded-full bg-[#f0791f]" />
            <span className="typing-dot h-2.5 w-2.5 rounded-full bg-[#f0791f]" />
            <span className="typing-dot h-2.5 w-2.5 rounded-full bg-[#f0791f]" />
          </div>
        )}
      </div>
    </AuthShell>
  );
}
